import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  type PricingToolDraft,
  useDeletePricingToolDraft,
  useGetPricingToolDraft,
  useSavePricingToolDraft,
} from '~/hooks/apis/offers/usePricingToolDraft';
import {
  type PricingToolSubmission,
  useGetLastConfirmedPricingToolSubmission,
} from '~/hooks/apis/offers/usePricingToolSubmissions';

import { useBlackouts } from './tabs/blackouts/useBlackouts';
import { useEgsDrafts } from './tabs/egs/useEgsDrafts';
import { useForecastingState } from './tabs/forecasting/useForecastingState';
import { describeIncompleteInclusion } from './tabs/inclusions/inclusionsModel';
import { useInclusionDrafts } from './tabs/inclusions/useInclusionDrafts';
import { describeInvalidOverrides } from './tabs/overrides/overridesValidation';
import { useOverrides } from './tabs/overrides/useOverrides';
import { withoutUnlinkedPackages } from './tabs/packageOptions/packageOptionsModel';
import { usePackageOptionsState } from './tabs/packageOptions/usePackageOptionsState';
import { useOtaSummary } from './tabs/pricingSummary/useOtaSummary';
import { netRateFor } from './tabs/propertySetup/propertySetupModel';
import { usePropertySetup } from './tabs/propertySetup/usePropertySetup';
import { completeSurchargeSession } from './tabs/surcharges/surchargeGridsModel';
import { describeIncompleteSurchargePeriod } from './tabs/surcharges/surchargePeriodValidation';
import { useSurchargeGrids } from './tabs/surcharges/useSurchargeGrids';
import type { PricingOptionIndex } from './types';

// Mounted once at PricingToolPage so state here survives tab switches.
interface PricingToolSessionValue {
  egs: ReturnType<typeof useEgsDrafts>;
  inclusions: ReturnType<typeof useInclusionDrafts>;
  propertySetup: ReturnType<typeof usePropertySetup>;
  blackouts: ReturnType<typeof useBlackouts>;
  overrides: ReturnType<typeof useOverrides>;
  packageOptions: ReturnType<typeof usePackageOptionsState>;
  preferredOption: PricingOptionIndex;
  setPreferredOption: (preferredOption: PricingOptionIndex) => void;
  surcharges: ReturnType<typeof useSurchargeGrids>;
  forecasting: ReturnType<typeof useForecastingState>;
  otaSummary: ReturnType<typeof useOtaSummary>;
  saveDraft: () => Promise<PricingToolDraft>;
  isSavingDraft: boolean;
  deleteDraft: () => Promise<unknown>;
  isDeletingDraft: boolean;
  hasDraft: boolean;
  draftIdBySfid: Record<string, string>;
  lastConfirmedSubmission: PricingToolSubmission | undefined;
  submissionHistoryStatus: 'pending' | 'error' | 'success';
}

const PricingToolSessionContext = createContext<PricingToolSessionValue | undefined>(undefined);

export function PricingToolSessionProvider({ offerId, children }: { offerId?: string; children: React.ReactNode }) {
  const inclusions = useInclusionDrafts(offerId);
  const egs = useEgsDrafts(offerId);
  const propertySetup = usePropertySetup(offerId);
  const blackouts = useBlackouts(offerId);
  const packageOptions = usePackageOptionsState(offerId);
  const overrides = useOverrides(offerId, packageOptions.activeOption);
  const surcharges = useSurchargeGrids(offerId);
  const forecasting = useForecastingState(offerId);
  const otaSummary = useOtaSummary(offerId);

  const netRate = propertySetup.fx.rate;
  const lastNetRate = useRef<number | undefined>(undefined);
  const { reapplyNetFormulaAtRate } = packageOptions;
  useEffect(() => {
    lastNetRate.current = undefined;
  }, [offerId]);
  useEffect(() => {
    if (!netRate || netRate <= 0) {
      return;
    }
    if (lastNetRate.current !== undefined && lastNetRate.current !== netRate) {
      reapplyNetFormulaAtRate({ fromRate: lastNetRate.current, toRate: netRate });
    }
    lastNetRate.current = netRate;
  }, [netRate, reapplyNetFormulaAtRate]);

  const { currency: fxCurrency, audToSourceCurrencyRate } = propertySetup.fx;
  const { reconvert: reconvertEgs } = egs;
  useEffect(() => {
    reconvertEgs(fxCurrency, audToSourceCurrencyRate);
  }, [fxCurrency, audToSourceCurrencyRate, reconvertEgs]);

  const [preferredOption, setPreferredOption] = useState<PricingOptionIndex>(0);
  useEffect(() => setPreferredOption(0), [offerId]);

  // version starts at 0 (no draft yet - PUT creates). Bumped from whatever the GET or the
  // last successful PUT reported, so concurrent-edit conflicts (409) surface on the next save.
  const draftVersionRef = useRef(0);
  const [hasDraft, setHasDraft] = useState(false);

  const { data: loadedDraft } = useGetPricingToolDraft(offerId);
  const loadedForOfferId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (loadedDraft === undefined || loadedForOfferId.current === offerId) {
      return;
    }
    loadedForOfferId.current = offerId;

    if (!loadedDraft) {
      draftVersionRef.current = 0;
      setHasDraft(false);
      return;
    }

    draftVersionRef.current = loadedDraft.version;
    setHasDraft(true);
    const { payload } = loadedDraft;

    if (payload.propertySetup) {
      propertySetup.loadValues(payload.propertySetup);
      lastNetRate.current = netRateFor(payload.propertySetup);
    }
    if (payload.blackouts) {
      const now = new Date().toISOString();
      blackouts.setBlackouts(payload.blackouts.map((draft) => ({ ...draft, createdAt: now, updatedAt: now })));
    }
    if (payload.overrides) {
      overrides.loadDraft(payload.overrides);
    }
    if (payload.surcharges) {
      surcharges.loadDraft(payload.surcharges);
    }
    if (payload.packageOptions) {
      packageOptions.loadDraft(payload.packageOptions);
    }
    if (payload.inclusions) {
      inclusions.loadDraft(payload.inclusions);
    }
    if (payload.forecasting) {
      forecasting.loadDraft(payload.forecasting);
    }
    if (payload.egs) {
      egs.loadDraft(payload.egs);
    }
    if (payload.otaSummary) {
      otaSummary.loadDraft(payload.otaSummary);
    }
    setPreferredOption(payload.preferredOption ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedDraft, offerId]);

  const { data: lastConfirmed, status: submissionHistoryStatus } = useGetLastConfirmedPricingToolSubmission(offerId);
  const lastConfirmedSubmission = lastConfirmed ?? undefined;
  // Only ids still in the draft: a link to a removed asset is rejected on submit.
  const draftIdBySfid = useMemo(() => {
    const draftIds = new Set([...packageOptions.roomAssets, ...inclusions.inclusionAssets].map(({ id }) => id));
    return Object.fromEntries(
      Object.entries(lastConfirmedSubmission?.salesforce_result?.asset_ids ?? {})
        .filter(([draftId]) => draftIds.has(draftId))
        .map(([draftId, sfid]) => [sfid, draftId]),
    );
  }, [lastConfirmedSubmission, packageOptions.roomAssets, inclusions.inclusionAssets]);

  const saveDraftMutation = useSavePricingToolDraft(offerId);
  const saveDraft = useCallback(() => {
    const invalidOverrides = describeInvalidOverrides(overrides.overrides);
    if (invalidOverrides) {
      return Promise.reject(new Error(invalidOverrides));
    }
    const incompleteInclusion = describeIncompleteInclusion(inclusions.drafts, packageOptions.optionStates);
    if (incompleteInclusion) {
      return Promise.reject(new Error(incompleteInclusion));
    }
    const incompleteSurchargePeriod = describeIncompleteSurchargePeriod(surcharges.session.grids.periods);
    if (incompleteSurchargePeriod) {
      return Promise.reject(new Error(incompleteSurchargePeriod));
    }
    return saveDraftMutation
      .mutateAsync({
        version: draftVersionRef.current,
        payload: {
          propertySetup: propertySetup.values ?? undefined,
          blackouts: blackouts.blackouts.map(({ id, name, startDate, endDate }) => ({
            id,
            name,
            startDate,
            endDate,
          })),
          overrides: overrides.overrides,
          preferredOption,
          surcharges: completeSurchargeSession(surcharges.session),
          packageOptions: {
            optionStates: withoutUnlinkedPackages(packageOptions.optionStates),
            roomAssets: packageOptions.roomAssets,
          },
          inclusions: {
            drafts: inclusions.drafts.map(({ createdAt: _createdAt, updatedAt: _updatedAt, ...draft }) => draft),
            inclusionAssets: inclusions.inclusionAssets,
          },
          forecasting: forecasting.session,
          egs: egs.drafts.map(
            ({ createdAt: _createdAt, updatedAt: _updatedAt, sourceName: _sourceName, ...draft }) => draft,
          ),
          otaSummary: Object.keys(otaSummary.options).length ? { options: otaSummary.options } : undefined,
        },
      })
      .then((draft) => {
        draftVersionRef.current = draft.version;
        setHasDraft(true);
        return draft;
      });
  }, [
    saveDraftMutation,
    propertySetup.values,
    blackouts.blackouts,
    overrides.overrides,
    preferredOption,
    surcharges.session,
    packageOptions.optionStates,
    packageOptions.roomAssets,
    inclusions.drafts,
    inclusions.inclusionAssets,
    forecasting.session,
    egs.drafts,
    otaSummary.options,
  ]);

  const deleteDraftMutation = useDeletePricingToolDraft(offerId);
  const deleteDraft = useCallback(
    () =>
      deleteDraftMutation.mutateAsync(draftVersionRef.current).then((result) => {
        setTimeout(() => window.location.reload(), 300);
        return result;
      }),
    [deleteDraftMutation],
  );

  const value = useMemo(
    () => ({
      egs,
      inclusions,
      propertySetup,
      blackouts,
      overrides,
      packageOptions,
      preferredOption,
      setPreferredOption,
      surcharges,
      forecasting,
      otaSummary,
      saveDraft,
      isSavingDraft: saveDraftMutation.isPending,
      deleteDraft,
      isDeletingDraft: deleteDraftMutation.isPending,
      hasDraft,
      draftIdBySfid,
      lastConfirmedSubmission,
      submissionHistoryStatus,
    }),
    [
      egs,
      inclusions,
      propertySetup,
      blackouts,
      overrides,
      packageOptions,
      preferredOption,
      surcharges,
      forecasting,
      otaSummary,
      saveDraft,
      saveDraftMutation.isPending,
      deleteDraft,
      deleteDraftMutation.isPending,
      hasDraft,
      draftIdBySfid,
      lastConfirmedSubmission,
      submissionHistoryStatus,
    ],
  );

  return <PricingToolSessionContext.Provider value={value}>{children}</PricingToolSessionContext.Provider>;
}

export function usePricingToolSession() {
  const context = useContext(PricingToolSessionContext);

  if (!context) {
    throw new Error('usePricingToolSession must be used within a PricingToolSessionProvider.');
  }

  return context;
}

export function useInclusionDraftsContext() {
  return usePricingToolSession().inclusions;
}

export function useEgsDraftsContext() {
  return usePricingToolSession().egs;
}

export function usePropertySetupContext() {
  return usePricingToolSession().propertySetup;
}

export function useBlackoutsContext() {
  return usePricingToolSession().blackouts;
}

export function useOverridesContext() {
  return usePricingToolSession().overrides;
}

export function usePackageOptionsContext() {
  return usePricingToolSession().packageOptions;
}

export function usePreferredOptionContext() {
  const { preferredOption, setPreferredOption } = usePricingToolSession();
  return { preferredOption, setPreferredOption };
}

export function useSurchargeGridsContext() {
  return usePricingToolSession().surcharges;
}

export function useForecastingContext() {
  return usePricingToolSession().forecasting;
}

export function useOtaSummaryContext() {
  return usePricingToolSession().otaSummary;
}

export function useSaveDraftContext() {
  const { saveDraft, isSavingDraft } = usePricingToolSession();
  return { saveDraft, isSavingDraft };
}

export function useDeleteDraftContext() {
  const { deleteDraft, isDeletingDraft, hasDraft } = usePricingToolSession();
  return { deleteDraft, isDeletingDraft, hasDraft };
}
